import EventEmitter from 'events';

import { makeRequest } from '../utils/makeRequest';
import { API_HOST } from '../envs';

export type CallerIdMap = {
  [phoneNumber: string]: string; // Maps phone numbers to caller IDs
};

export type LineNameMap = {
  [phoneNumber: string]: string; // Maps phone numbers to line names
};

interface CallerIdResponse {
  caller_id: string;
  line_name?: string;
}

const STORAGE_KEY = 'ergeon_caller_id_overrides';
const LINE_NAME_STORAGE_KEY = 'ergeon_line_name_overrides';

export default class CallerIdService extends EventEmitter {
  private callerIds: CallerIdMap = {};
  private lineNames: LineNameMap = {};
  private pendingRequests = new Map<string, Promise<{callerId?: string; lineName?: string}>>();

  constructor() {
    super();
    this.loadFromStorage();
  }

  // Load caller ID settings from session storage
  private loadFromStorage(): void {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        this.callerIds = JSON.parse(stored);
      }
    } catch (error) {
      console.error('Failed to load caller ID settings:', error);
      this.callerIds = {};
    }

    try {
      const storedLineNames = sessionStorage.getItem(LINE_NAME_STORAGE_KEY);
      if (storedLineNames) {
        this.lineNames = JSON.parse(storedLineNames);
      }
    } catch (error) {
      console.error('Failed to load line name settings:', error);
      this.lineNames = {};
    }
  }

  // Save caller ID settings to session storage
  private saveToStorage(): void {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.callerIds));
      sessionStorage.setItem(LINE_NAME_STORAGE_KEY, JSON.stringify(this.lineNames));
      this.emit('change', this.callerIds);
    } catch (error) {
      console.error('Failed to save caller ID settings:', error);
    }
  }

  // Set a global caller ID override (applies to all numbers)
  setGlobalCallerId(callerId: string): void {
    this.callerIds['*'] = callerId;
    this.saveToStorage();
  }

  // Set a number-specific caller ID override
  setNumberCallerId(phoneNumber: string, callerId: string): void {
    this.callerIds[phoneNumber] = callerId;
    this.saveToStorage();
  }

  async fetchCallerId(phoneNumber: string): Promise<{callerId?: string; lineName?: string}> {
    try {
      const params = new URLSearchParams({
        phone_number: phoneNumber,
      });

      const data = await makeRequest<CallerIdResponse>(`${API_HOST}/api/phoneapp/callerid/?${params.toString()}`, {
        useAuthService: true,
      });

      return {
        callerId: data.caller_id,
        lineName: data.line_name,
      };
    } catch (error) {
      console.error('Failed to fetch caller ID from API:', error);
      return {};
    }
  }

  // Get the caller ID and line name for a specific number
  async getCallerId(phoneNumber: string): Promise<{callerId?: string; lineName?: string}> {
    // First check for number-specific override
    if (this.callerIds[phoneNumber]) {
      return {
        callerId: this.callerIds[phoneNumber],
        lineName: this.lineNames[phoneNumber],
      };
    }

    // Then check for global override
    if (this.callerIds['*']) {
      return {
        callerId: this.callerIds['*'],
        lineName: this.lineNames['*'],
      };
    }

    // Dedupe: return existing request if already in flight
    const pending = this.pendingRequests.get(phoneNumber);
    if (pending) {
      return pending;
    }

    // Create and track the request
    const request = this.fetchCallerId(phoneNumber).then(({ callerId, lineName }) => {
      this.pendingRequests.delete(phoneNumber);
      if (callerId) {
        this.setNumberCallerId(phoneNumber, callerId);
      }
      if (lineName) {
        this.lineNames[phoneNumber] = lineName;
        this.saveToStorage();
      }
      return { callerId, lineName };
    }).catch((error) => {
      this.pendingRequests.delete(phoneNumber);
      throw error;
    });

    this.pendingRequests.set(phoneNumber, request);
    return request;
  }

  // Get all caller ID overrides
  getAllCallerIds(): CallerIdMap {
    return { ...this.callerIds };
  }

  // Clear all caller ID overrides
  clearAll(): void {
    this.callerIds = {};
    this.saveToStorage();
  }
}
